import { Stars03 } from '@kula-ai/icons-core';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useAccountUsersFieldConfig,
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useInterviewerPoolConfig,
} from '@/formConfigs';
import { INTERVIEW_ACTIVITY_COPY } from '../copy';
import { useIntegrations } from '@/hooks/use-integrations';
import { useHackerRankTemplates } from './useHackerRankTemplates';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useAllSharedCalendarsQuery } from '@/api/sharedCalendars';
import { useMailBox } from '@/hooks/use-mailbox';
import { useKFlags } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';

const COPY = INTERVIEW_ACTIVITY_COPY.details;

const INTERVIEW_NAME_VARIABLES = {
  org: ['org.name'],
  job: ['job.title', 'job.current_stage'],
  candidate: [
    'candidate.first_name',
    'candidate.last_name',
    'candidate.full_name',
  ],
};

export const useInterviewActivityDetailsSchema = (options?: {
  stageName?: string;
  selectedActivity?: string;
  participantDefaultOptions?: Array<{
    value: string;
    label: string;
    description?: string;
    image?: string;
  }>;
}) => {
  const { stageName, selectedActivity, participantDefaultOptions } =
    options ?? {};
  const { isHackerRankEnabled } = useIntegrations();
  const isHackerRankActivity = selectedActivity === 'hackerrank_interview';

  // Job-level note_taker mandatory flag — forces notetaker on when set
  const { currentJob = {} } = useSelector(getAtsJobCreate) as {
    currentJob?: { data?: { note_taker?: { mandatory?: boolean } } };
  };
  const noteTakerMandatory = Boolean(currentJob?.data?.note_taker?.mandatory);

  const { fieldConfig: interviewerField } = useAccountUsersFieldConfig({
    name: 'participant_ids',
    label: 'Add interviewers',
    placeholder: 'Select interviewer',
    condition: {
      operator: 'or',
      conditions: [
        {
          operator: 'and',
          conditions: [
            { operator: 'equal', field: 'interview_type', value: 'one_on_one' },
            {
              operator: 'equal',
              field: 'interviewTypeRadio',
              value: 'manual_selection',
            },
          ],
        },
        { operator: 'equal', field: 'interview_type', value: 'panel' },
      ],
    },
    fieldProps: participantDefaultOptions?.length
      ? { defaultOptions: participantDefaultOptions }
      : undefined,
  });

  // HackerRank template options — fetched first so the location field
  // can read the `userHasNoAccount` signal and disable the HR option
  // when the current user lacks a personal HR for Work account.
  const {
    templateOptions: hackerRankTemplateOptions,
    isLoading: hrLoading,
    userHasNoAccount: hrUserHasNoAccount,
  } = useHackerRankTemplates({ enabled: Boolean(isHackerRankEnabled) });

  const { fieldConfig: locationField, defaultLocation } =
    useLocationFieldConfig({
      isHackerRank: isHackerRankActivity,
      hackerRankUserHasNoAccount: hrUserHasNoAccount,
    });

  const { fieldConfig: officeField } = useOfficeFieldConfig({});

  const interviewerPoolFields = useInterviewerPoolConfig();

  // ── Shared-calendar selection ──────────────────────────────────────────
  // Mirrors `useScheduleCalendarData` from the Schedule wizard: gated on
  // the same workspace flag + calendar connection so both surfaces show
  // the row consistently. We DON'T also gate on
  // `interviewPlan?.shared_calendar_enabled` (the wizard's third
  // condition) because the Plan form IS the place where that opt-in is
  // first authored — gating on it would prevent the user from ever
  // turning it on. BE permits `calendar_info: { provider_calendar_id,
  // is_primary }` on the Job Stage Action endpoint (CORE-4039 →
  // ats_job_stage_actions_controller#ats_job_stage_action_params).
  const { feSchedulingPreference } = useKFlags() as {
    feSchedulingPreference?: boolean;
  };
  const currUser = useSelector(selectCurrUser) as
    | { connected_calendar?: { gcal?: boolean; mscal?: boolean } }
    | null
    | undefined;
  const hasConnectedCalendar = Boolean(
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal
  );
  const { isCalConnected } = useMailBox();
  const shouldShowCalendarRow = Boolean(
    feSchedulingPreference && hasConnectedCalendar
  );

  // Plan editor uses the unfiltered index endpoint so a coordinator
  // can preselect a teammate's calendar even though they themselves
  // don't have edit access. The runtime scheduling surface
  // (`useScheduleCalendarData`) keeps the access-filtered list — that
  // gate is the right one at schedule-time.
  const { data: sharedCalendars, isLoading: isCalendarsLoading } =
    useAllSharedCalendarsQuery({
      enabled: shouldShowCalendarRow && isCalConnected,
    });

  const calendars = useMemo(
    () =>
      (sharedCalendars ?? []).filter(
        (c) => c?.status !== 'deleted_from_provider'
      ),
    [sharedCalendars]
  );

  // Default calendar: the user's own primary, or whichever the BE marked
  // default. Used to seed the form when the plan has no `calendar_info`
  // saved yet (create + edit-of-legacy-plan).
  const defaultCalendar = useMemo(
    () => calendars.find((c) => c.is_default || c.is_primary),
    [calendars]
  );

  const calendarOptions = useMemo(
    () =>
      calendars
        .filter(
          (c): c is typeof c & { provider_calendar_id: string } =>
            typeof c.provider_calendar_id === 'string'
        )
        .map((c) => ({
          value: c.provider_calendar_id,
          label: `${c.name ?? c.provider_calendar_id}${c.is_primary ? ' (You)' : ''}`,
        })),
    [calendars]
  );

  const schema = useMemo(
    () => [
      {
        type: 'sectionLabel',
        label: COPY.sectionLabel,
      },
      {
        type: 'variableInput' as const,
        name: 'interviewName',
        label: 'Interview name',
        required: true,
        placeholder: 'Add interview name here',
        maxLength: 250,
        fieldProps: {
          variables: INTERVIEW_NAME_VARIABLES,
        },
      },
      {
        type: 'select' as const,
        name: 'interview_type',
        label: 'Interview type',
        options: [
          {
            value: 'one_on_one',
            label: 'One-on-one interview',
            description:
              'Add the interviewer you want or pick an interviewer pool',
          },
          {
            value: 'panel',
            label: 'Panel interview',
            description:
              'Add all the interviewers you want to attend this interview',
          },
        ],
      },
      {
        type: 'radio' as const,
        name: 'interviewTypeRadio',
        condition: {
          operator: 'equal',
          field: 'interview_type',
          value: 'one_on_one',
        },
        options: [
          {
            value: 'manual_selection',
            label: 'Interviewers',
            description: 'Manually select interviewers for this interview',
          },
          {
            value: 'interview_pool',
            label: 'Interviewer pool',
            description: 'Select interviewers from an interviewer pool',
          },
        ],
      },
      interviewerField,
      ...interviewerPoolFields,
      {
        type: 'sectionLabel',
        label: COPY.scheduleSectionLabel,
      },
      // Shared-calendar picker (top of "Schedule details", before duration).
      // Persisted as `calendar_info: { provider_calendar_id, is_primary }`
      // — same shape the Schedule wizard already prefills from on
      // `fromPlan.ts`, so picking a calendar here propagates to every
      // interview created from this plan with no other wiring.
      ...(shouldShowCalendarRow
        ? [
            {
              type: 'select' as const,
              name: 'calendar_info_picker',
              label: 'Calendar',
              placeholder: "Organizer's calendar",
              options: calendarOptions,
              fieldProps: {
                loading: isCalendarsLoading,
                searchable: false,
              },
              // The Select's value is the provider id (a string). We
              // additionally write the structured `calendar_info` object
              // — that's the field the payload mapper + BE expects.
              // Mirrors the wizard's `scheduleDetailsSchema.tsx` onChange.
              onChange: (
                value: unknown,
                formMethods: {
                  setValue: (_n: string, _v: unknown, _o?: unknown) => void;
                }
              ) => {
                const id = Array.isArray(value)
                  ? value[0]
                  : (value as string | undefined);
                if (typeof id !== 'string' || !id) return;
                const cal = calendars.find(
                  (c) => c.provider_calendar_id === id
                );
                formMethods.setValue('calendar_info', {
                  provider_calendar_id: id,
                  is_primary: Boolean(cal?.is_primary),
                });
              },
            },
          ]
        : []),
      {
        type: 'numberStepper' as const,
        name: 'duration',
        label: COPY.duration.label,
        min: 5,
        max: 1440,
        fieldProps: {
          step: 15,
          min: 5,
          max: 1440,
          unitLabel: COPY.duration.unit,
        },
      },
      {
        type: 'switch' as const,
        name: 'calendar_visibility',
        fieldProps: {
          switchLabel: COPY.calendarVisibility.label,
          description: COPY.calendarVisibility.subLabel,
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      locationField,
      officeField,
      {
        type: 'select' as const,
        name: 'ext_interview_template_id',
        label: 'HackerRank Template',
        placeholder: 'Select template',
        options: hackerRankTemplateOptions,
        loading: hrLoading,
        // Required when the conditional fires (location=hackerrank).
        // FormBuilder's schemaGenerator runs the required check inside
        // the `superRefine` for conditional fields, so the validation
        // only triggers when the user actually picks HackerRank.
        required: 'HackerRank template is required',
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'hackerrank',
        },
      },
      {
        type: 'select' as const,
        name: 'host',
        label: COPY.host.label,
        tooltip: COPY.host.tooltip,
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'zoom',
        },
        options: [
          {
            label: COPY.host.options.organizer,
            value: 'organizer',
          },
          {
            label: COPY.host.options.interviewer,
            value: 'one_of_the_interviewers',
          },
        ],
      },
      {
        type: 'switch' as const,
        name: 'enable_note_taker',
        condition: {
          operator: 'in',
          field: 'location',
          value: ['zoom', 'google_meet', 'teams'],
        },
        disabled: noteTakerMandatory,
        classNames: {
          container:
            'bg-secondary-25 p-4 rounded-lg border border-secondary-100',
        },
        fieldProps: {
          switchLabel: 'Enable AI notetaker?',
          description: noteTakerMandatory
            ? 'AI notetaker is mandatory for this job.'
            : 'The AI Notetaker automatically captures key points and summaries during meetings. It organizes and highlights information for easy review and follow-up.',
          labelLeftElement: React.createElement(Stars03, {
            size: 18,
            className: '!text-primary-600',
          }),
          fullWidth: true,
          labelPosition: 'left',
        },
      },
    ],
    [
      interviewerField,
      interviewerPoolFields,
      locationField,
      officeField,
      hackerRankTemplateOptions,
      hrLoading,
      noteTakerMandatory,
      shouldShowCalendarRow,
      calendarOptions,
      isCalendarsLoading,
      calendars,
    ]
  );

  const defaultValues = useMemo(
    () => ({
      interviewName: stageName ? `Interview - ${stageName}` : '',
      interview_type: 'one_on_one',
      interviewTypeRadio: 'manual_selection',
      participant_ids: [],
      interview_pool_id: '',
      duration: 30,
      schedule_selection: 'manual',
      calendar_visibility: false,
      office: '',
      host: 'organizer',
      location: defaultLocation,
      enable_note_taker: noteTakerMandatory,
      // `undefined` (not `null`) — FormBuilder's Zod union for the
      // conditional `required` rejects `null`.
      ext_interview_template_id: undefined,
      // Seed both the picker (string) and the persisted shape (object)
      // so the form initialises with the user's primary/default calendar
      // — without this, create-mode would always submit an empty
      // calendar_info even if the recruiter never touched the field.
      calendar_info_picker: defaultCalendar?.provider_calendar_id ?? '',
      calendar_info: defaultCalendar?.provider_calendar_id
        ? {
            provider_calendar_id: defaultCalendar.provider_calendar_id,
            is_primary: Boolean(defaultCalendar.is_primary),
          }
        : undefined,
    }),
    [stageName, defaultLocation, noteTakerMandatory, defaultCalendar]
  );

  return { schema, defaultValues, defaultLocation, defaultCalendar };
};
